import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileUploader, LoadPanel } from 'devextreme-react'
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  Form,
  FormRef,
  GroupItem,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form'
import { LoadPanelRef } from 'devextreme-react/cjs/load-panel'
import { ValidationCallbackData } from 'devextreme/common'
import { DateBoxTypes } from 'devextreme-react/cjs/date-box'
import { SwitchTypes } from 'devextreme-react/cjs/switch'
import { TagBoxTypes } from 'devextreme-react/cjs/tag-box'
import type { PopupRef } from 'devextreme-react/cjs/popup'
import { Scheduler, type SchedulerRef, type SchedulerTypes } from 'devextreme-react/scheduler'
import type { AxiosInstance } from 'axios'
import saveAs from 'file-saver'
import { ArraySourceComposer } from '@/auth/api/connector'
import notify from 'devextreme/ui/notify'
import type { OptionChangedEvent, ShownEvent, ToolbarItem } from 'devextreme/ui/popup'
import {
  completeDateBoxTimeOnTab,
  createTime,
  customButton,
  isUserRoleAllowed,
  onInitializedHtmlEditor,
  onValueChangedHtmlEditor,
  toolbarHtmlEditor,
} from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import type { AzureUserInfo } from '@/auth/azure/azureManager'
import type { useScreenSize } from '@/themes/media-query'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import type { task } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  canManageAppointment,
  combineDateAndTime,
  getRecurrenceLabel,
  toDateOnly,
  toEmailStrings,
} from '@/routes/calendar/CalendarPage.utils'
import type {
  CalendarAttachmentDto,
  CalendarRoom,
  ExtendedAppointment,
  FormAppointment,
  FormLocation,
  popupStateType,
  RoomResponseDTO,
} from '@/routes/calendar/CalendarPage.types'
import { FileUploaderRef } from 'devextreme-react/file-uploader'

export type AppointmentPopupFormProps = {
  state: popupStateType
  users: (user | CalendarRoom)[]
  userInfo: AzureUserInfo | undefined
  rooms: CalendarRoom[]
  schedulerRef: RefObject<SchedulerRef | null>
  popupRef: RefObject<PopupRef | null>
  loadWithPending: (pendingItems: ExtendedAppointment[]) => void
  client: AxiosInstance
  currentScreenSize: ReturnType<typeof useScreenSize>
  saveRef?: RefObject<AppointmentPopupFormRef | null>
  onSaveDisabledChange?: (disabled: boolean) => void
}

export type AppointmentPopupFormRef = {
  save: () => Promise<void>
}

type NewAttachmentItem = {
  kind: 'new'
  key: string
  name: string
  size: number
}

type ExistingAttachmentItem = {
  kind: 'existing'
  key: string
  id: string
  name: string
  size: number
  attachmentKind: 'file' | 'item'
}

export type OpenableAttachment = {
  id: string
  name: string
  attachmentKind: 'file' | 'item'
}

type AttachmentListItem = ExistingAttachmentItem | NewAttachmentItem

type AttachmentSectionHandle = {
  getNewFiles: () => File[]
  getRemovedAttachmentIds: () => string[]
  hasVisibleAttachments: () => boolean
  setExistingAttachments: (attachments: CalendarAttachmentDto[]) => void
  setLoading: (loading: boolean) => void
}

type AttachmentSectionProps = {
  resetKey: string
  editEnabled: boolean
  creating: boolean
  hasAttachments?: boolean
  onRequestExistingAttachments: () => void
  onOpenExistingAttachment: (attachment: OpenableAttachment) => void
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const buildNewAttachmentKey = (file: File) => `new-${file.name}-${file.size}-${file.lastModified}`

const RECURRENCE_POPUP_TITLE = 'Serie'
// Durata fittizia dell'appuntamento usato solo per aprire il form ricorrenza
const RECURRENCE_PLACEHOLDER_DURATION_MS = 30 * 60 * 1000

const AttachmentSection = forwardRef<AttachmentSectionHandle, AttachmentSectionProps>(
  (
    { resetKey, editEnabled, creating, hasAttachments, onRequestExistingAttachments, onOpenExistingAttachment },
    ref,
  ) => {
    const fileUploaderRef = useRef<FileUploaderRef>(null)
    const detailsRef = useRef<HTMLDetailsElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const loadingRef = useRef<HTMLDivElement>(null)
    const emptyRef = useRef<HTMLDivElement>(null)
    const removedAttachmentIdsRef = useRef<string[]>([])
    const newFilesRef = useRef<File[]>([])
    const existingAttachmentsRef = useRef<CalendarAttachmentDto[]>([])
    const requestedExistingAttachmentsRef = useRef(false)
    const loadingStateRef = useRef(false)

    const syncAttachmentUi = useCallback(() => {
      const currentList = listRef.current
      const currentLoading = loadingRef.current
      const currentEmpty = emptyRef.current
      if (!currentList || !currentLoading || !currentEmpty) return

      const existingItems: ExistingAttachmentItem[] = existingAttachmentsRef.current.map((attachment) => ({
        kind: 'existing',
        key: `existing-${attachment.id}`,
        id: attachment.id,
        name: attachment.name,
        size: attachment.size,
        attachmentKind: attachment.kind ?? 'file',
      }))
      const newItems: NewAttachmentItem[] = newFilesRef.current.map((file) => ({
        kind: 'new',
        key: buildNewAttachmentKey(file),
        name: file.name,
        size: file.size,
      }))
      const attachmentItems: AttachmentListItem[] = [...existingItems, ...newItems]

      currentList.replaceChildren()
      attachmentItems.forEach((attachment) => {
        const item = document.createElement('div')
        item.className = 'calendar-attachment-item'

        if (attachment.kind === 'existing' && attachment.attachmentKind === 'item') {
          const icon = document.createElement('span')
          icon.className = 'calendar-attachment-icon'
          icon.setAttribute('aria-hidden', 'true')
          icon.textContent = '✉'
          item.appendChild(icon)
        }

        const name = document.createElement('span')
        name.className = 'calendar-attachment-name'
        name.title = attachment.name
        name.textContent = attachment.name
        if (attachment.kind === 'existing') {
          name.classList.add('calendar-attachment-name--clickable')
          name.setAttribute('role', 'button')
          name.tabIndex = 0
          name.title = attachment.attachmentKind === 'item' ? 'Scarica email (.eml)' : 'Scarica allegato'
          const openAttachment = () =>
            onOpenExistingAttachment({
              id: attachment.id,
              name: attachment.name,
              attachmentKind: attachment.attachmentKind,
            })
          name.onclick = openAttachment
          name.onkeydown = (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              openAttachment()
            }
          }
        }
        item.appendChild(name)

        const size = document.createElement('span')
        size.className = 'calendar-attachment-size'
        size.textContent = formatFileSize(attachment.size)
        item.appendChild(size)

        if (editEnabled) {
          const removeButton = document.createElement('button')
          removeButton.type = 'button'
          removeButton.className = 'calendar-attachment-remove'
          removeButton.title = 'Rimuovi allegato'
          removeButton.textContent = '✕'
          removeButton.onclick = () => {
            if (attachment.kind === 'existing') {
              existingAttachmentsRef.current = existingAttachmentsRef.current.filter((a) => a.id !== attachment.id)
              removedAttachmentIdsRef.current = [...removedAttachmentIdsRef.current, attachment.id]
            } else {
              newFilesRef.current = newFilesRef.current.filter((file) => buildNewAttachmentKey(file) !== attachment.key)
            }
            syncAttachmentUi()
          }
          item.appendChild(removeButton)
        }

        currentList.appendChild(item)
      })

      currentList.style.display = attachmentItems.length > 0 ? 'flex' : 'none'
      currentLoading.style.display = loadingStateRef.current ? 'block' : 'none'
      currentEmpty.style.display =
        !loadingStateRef.current && attachmentItems.length === 0 && !creating && !hasAttachments ? 'block' : 'none'
    }, [creating, editEnabled, hasAttachments, onOpenExistingAttachment])

    useImperativeHandle(
      ref,
      () => ({
        getNewFiles: () => [...newFilesRef.current],
        getRemovedAttachmentIds: () => [...removedAttachmentIdsRef.current],
        hasVisibleAttachments: () => existingAttachmentsRef.current.length + newFilesRef.current.length > 0,
        setExistingAttachments: (attachments: CalendarAttachmentDto[]) => {
          existingAttachmentsRef.current = attachments
          syncAttachmentUi()
        },
        setLoading: (loading: boolean) => {
          loadingStateRef.current = loading
          syncAttachmentUi()
        },
      }),
      [syncAttachmentUi],
    )

    useEffect(() => {
      existingAttachmentsRef.current = []
      removedAttachmentIdsRef.current = []
      newFilesRef.current = []
      loadingStateRef.current = false
      requestedExistingAttachmentsRef.current = false
      if (detailsRef.current) detailsRef.current.open = false
      fileUploaderRef.current?.instance().reset()
      syncAttachmentUi()
    }, [resetKey])

    const handleNewFilesSelected = useCallback(
      (files: File[]) => {
        if (files.length === 0) return

        const existingKeys = new Set(newFilesRef.current.map((file) => buildNewAttachmentKey(file)))
        const filesToAdd: File[] = []
        const itemsToAdd: NewAttachmentItem[] = []

        files.forEach((file) => {
          const key = buildNewAttachmentKey(file)
          if (existingKeys.has(key)) return
          existingKeys.add(key)
          filesToAdd.push(file)
          itemsToAdd.push({
            kind: 'new',
            key,
            name: file.name,
            size: file.size,
          })
        })

        if (itemsToAdd.length === 0) {
          fileUploaderRef.current?.instance().reset()
          return
        }

        newFilesRef.current = [...newFilesRef.current, ...filesToAdd]
        if (detailsRef.current) detailsRef.current.open = true
        syncAttachmentUi()
        fileUploaderRef.current?.instance().reset()
      },
      [syncAttachmentUi],
    )

    const handleSummaryClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault()
        const details = detailsRef.current
        if (!details) return
        const nextExpanded = !details.open
        details.open = nextExpanded
        if (nextExpanded && !creating && hasAttachments && !requestedExistingAttachmentsRef.current) {
          requestedExistingAttachmentsRef.current = true
          onRequestExistingAttachments()
        }
      },
      [creating, hasAttachments, onRequestExistingAttachments],
    )

    return (
      <details ref={detailsRef} className="calendar-attachments-details">
        <summary
          className={`calendar-section-summary${hasAttachments ? ' calendar-section-summary--success' : ''}`}
          onClick={handleSummaryClick}
        >
          Allegati
        </summary>
        <div className="calendar-attachments-section">
          <div ref={loadingRef} className="calendar-attachments-loading" style={{ display: 'none' }}>
            Caricamento allegati...
          </div>
          <div ref={listRef} className="calendar-attachments-list" style={{ display: 'none' }} />
          <div ref={emptyRef} className="calendar-attachments-empty" style={{ display: 'none' }}>
            Nessun allegato
          </div>
          {editEnabled && (
            <FileUploader
              ref={fileUploaderRef}
              name="calendar_attachments"
              selectButtonText="Seleziona file"
              labelText="o trascina i file qui"
              multiple={true}
              accept="*"
              uploadMode="useForm"
              maxFileSize={10485760}
              invalidMaxFileSizeMessage="File troppo grande: limite 10 MB"
              readyToUploadMessage=""
              onValueChanged={(e) => {
                handleNewFilesSelected((e.value as File[]) ?? [])
              }}
            />
          )}
        </div>
      </details>
    )
  },
)

AttachmentSection.displayName = 'AttachmentSection'

const AppointmentPopupForm = ({
  state,
  users,
  userInfo,
  rooms,
  schedulerRef,
  popupRef,
  loadWithPending,
  client,
  currentScreenSize,
  saveRef,
  onSaveDisabledChange,
}: AppointmentPopupFormProps) => {
  const { service } = useQsAdminApiManager()
  const roomsDataRef = useRef(rooms)
  const formRef = useRef<FormRef>(null)
  const loadPanelRef = useRef<LoadPanelRef>(null)
  const attachmentSectionRef = useRef<AttachmentSectionHandle>(null)
  const attachmentsRequestKeyRef = useRef<string | null>(null)
  const recurrenceSchedulerRef = useRef<SchedulerRef>(null)
  const originalRecurrenceRuleRef = useRef('')
  const recurrenceEditedRef = useRef(false)
  const foundUser = users.find(
    (item) => item.email === ('email' in state.editData ? state.editData.email : userInfo?.email),
  )
  const createCustomLocationItem = (text: string) => ({
    userid: `${text}@${text}`,
    email: null,
    fullname: text,
    isBusy: false,
    isAvailable: true,
  })
  const createCustomAttendee = (email: string): user => {
    let generatedId = 0
    for (let i = 0; i < email.length; i++) {
      generatedId = (generatedId * 31 + email.charCodeAt(i)) | 0
    }
    if (generatedId >= 0) generatedId = -generatedId - 1

    return {
      id: generatedId,
      fullname: email,
      cognome: null,
      nome: null,
      email,
      note: null,
      attivo: false,
      contractor: false,
      email_ad: null,
    }
  }

  const normalizeLocationValue = (location: FormLocation | string | undefined) => {
    if (!location) return undefined
    if (typeof location === 'string') return createCustomLocationItem(location)
    return location
  }

  // Function to fetch room availability based on date/time
  const fetchRoomAvailability = async (startDate: Date, startTime: Date, endDate: Date, endTime: Date) => {
    try {
      // Combine date and time
      const start = combineDateAndTime(startDate, startTime)

      const end = combineDateAndTime(endDate, endTime)

      // Call API to get updated room availability
      const response = await client.post(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/rooms`, {
        startDateTime: start.toISOString(),
        endDateTime: end.toISOString(),
      })

      const updatedRooms: CalendarRoom[] = response.data.data
        .map((room: RoomResponseDTO) => ({
          userid: room.id,
          fullname: room.fullName,
          email: room.email,
          status: room.status,
          isAvailable: room.isAvailable,
        }))
        .sort((a: CalendarRoom, b: CalendarRoom) => a.fullname.localeCompare(b.fullname))

      // Update ref
      roomsDataRef.current = updatedRooms

      //Update the location editor's dataSource directly
      const locationEditor = formRef.current?.instance().getEditor('location')
      if (locationEditor) {
        locationEditor.option('dataSource', ArraySourceComposer('userid', updatedRooms))
      }
    } catch (error) {
      console.error('Error fetching room availability:', error)
      notify('Errore nel recupero della disponibilità delle stanze', 'error', 2000)
    } finally {
      // setIsLoadingRooms(false)
    }
  }
  // Handler for date/time changes
  const handleDateTimeChange = () => {
    const formData = formRef.current?.instance().option('formData')
    if (formData) {
      const startDate = formData['start/date']
      const startTime = formData['start/time']
      const endDate = formData['end/date']
      const endTime = formData['end/time']
      const allDay = formData['allDay']

      if (startDate && startTime && endDate && endTime) {
        fetchRoomAvailability(
          new Date(startDate),
          new Date(startTime),
          !allDay
            ? new Date(endDate)
            : new Date(new Date(endDate).getFullYear(), new Date(endDate).getMonth(), new Date(endDate).getDate() + 1),
          new Date(endTime),
        )
      }
    }
  }
  const foundRequiredAttendees = users.filter((item) => state.editData.requiredAttendees?.includes(item.email ?? ''))
  const externalRequiredAttendees: string[] =
    state.editData.requiredAttendees?.filter((reqAtt: string) => !users.find((user) => user.email === reqAtt)) ?? []
  externalRequiredAttendees?.map((ext) => {
    const reqAtt = createCustomAttendee(ext)
    foundRequiredAttendees.push(reqAtt)
  })

  const foundOptionalAttendees = users.filter((item) => state.editData.optionalAttendees?.includes(item.email ?? ''))
  const externalOptionalAttendees: string[] =
    state.editData.optionalAttendees?.filter((optAtt: string) => !users.find((user) => user.email === optAtt)) ?? []
  externalOptionalAttendees?.map((ext) => {
    const optAtt = createCustomAttendee(ext)
    foundOptionalAttendees.push(optAtt)
  })

  const editEnabled = canManageAppointment(
    userInfo?.roles,
    userInfo?.email,
    foundUser?.email,
    state.editData.isOrganizer,
  )
  const canEditTaskLink = isUserRoleAllowed(userInfo?.roles, [
    Roles.GlobalAdministrator,
    Roles.Administrator,
    Roles.Supervisor,
    Roles.Organizer,
  ])
  const tasksQuery = useQuery({
    queryKey: ['calendar-popup-tasks'],
    queryFn: async () => {
      const response = await service.task().query((builder, taskBuilder) => {
        builder.select('id', 'ded_Dis')
        builder.orderBy(taskBuilder.ded_Dis.desc())
      })
      return response.data.value as task[]
    },
    staleTime: 60 * 60 * 1000,
  })

  // Durata originale in ms — usata per preservare la durata quando si modifica l'orario di inizio
  const originalDurationMs = (() => {
    const st = state.editData['start/time']
    const et = state.editData['end/time']
    if (!st || !et) return 30 * 60000
    const diff = new Date(et).getTime() - new Date(st).getTime()
    return diff > 0 ? diff : 30 * 60000
  })()

  // Durata originale in giorni — usata per preservare la durata negli eventi giornata intera in modifica
  const originalDurationDays = (() => {
    const sd = state.editData['start/date']
    const ed = state.editData['end/date']
    if (!sd || !ed) return 0
    const diff = toDateOnly(new Date(ed)).getTime() - toDateOnly(new Date(sd)).getTime()
    return diff > 0 ? Math.round(diff / 86400000) : 0
  })()

  // Traccia visibilità del campo facoltativi senza causare re-render del form
  const showOptionalRef = useRef<boolean>((state.editData.optionalAttendees?.length ?? 0) > 0)

  // Disabilita Teams solo se non ci sono partecipanti né richiesti né facoltativi
  const updateTeamsFlag = () => {
    const formData = formRef.current?.instance().option('formData')
    const required: user[] = (formData?.requiredAttendees as unknown as user[]) ?? []
    const optional: user[] = (formData?.optionalAttendees as unknown as user[]) ?? []
    if (required.length === 0 && optional.length === 0) {
      formRef.current?.instance().updateData('isOnlineMeeting', false)
    }
  }

  // Builder del bottone toggle facoltativi — usa la utility customButton come gli altri campi
  const buildOptionalToggleButton = (visible: boolean) =>
    customButton(
      visible ? 'remove' : 'add',
      handleToggleOptionalAttendees,
      !editEnabled,
      visible ? 'Togli facoltativi' : 'Aggiungi facoltativi',
    )

  // Handler del bottone: toglie/mostra il campo facoltativi via DevExtreme API
  const handleToggleOptionalAttendees = () => {
    const newVisible = !showOptionalRef.current
    showOptionalRef.current = newVisible
    formRef.current?.instance().itemOption('optionalAttendees', 'visible', newVisible)
    if (!newVisible) {
      formRef.current?.instance().updateData('optionalAttendees', [])
      updateTeamsFlag()
    }
    const tagBox = formRef.current?.instance().getEditor('requiredAttendees')
    tagBox?.option('buttons', [buildOptionalToggleButton(newVisible)])
  }

  const appointment: FormAppointment = {
    ...state.editData,
    user: foundUser,
    requiredAttendees: foundRequiredAttendees as unknown as string[],
    optionalAttendees: foundOptionalAttendees as unknown as string[],
  }

  const memoizedAppointment = useMemo<FormAppointment>(
    () => appointment,
    [state.editData, foundUser, foundRequiredAttendees, foundOptionalAttendees],
  )

  const completeTimeOnTab = (field: 'start/time' | 'end/time', e: DateBoxTypes.KeyDownEvent) => {
    const parsedTime = completeDateBoxTimeOnTab(e)
    if (!parsedTime) return

    formRef.current?.instance().updateData(field, parsedTime)

    if (field === 'start/time') {
      const durationMs = state.creating ? 30 * 60000 : originalDurationMs
      formRef.current?.instance().updateData('end/time', new Date(parsedTime.getTime() + durationMs))
    }

    if (!appointment.allDay) handleDateTimeChange()
  }

  const attachmentsRequestKey =
    !state.creating && state.editData.hasAttachments && state.editData.email && state.editData.appointmentId
      ? `${state.editData.email}__${state.editData.appointmentId}`
      : null

  const requestExistingAttachments = useCallback(() => {
    if (!attachmentsRequestKey || !state.editData.email || !state.editData.appointmentId) return

    attachmentsRequestKeyRef.current = attachmentsRequestKey
    attachmentSectionRef.current?.setExistingAttachments([])
    attachmentSectionRef.current?.setLoading(true)

    client
      .get(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/attachments`, {
        params: { email: state.editData.email, appointmentId: state.editData.appointmentId },
      })
      .then((response) => {
        if (attachmentsRequestKeyRef.current !== attachmentsRequestKey) return
        const attachments = response.data?.data ?? []
        attachmentSectionRef.current?.setExistingAttachments(attachments)
      })
      .catch((error) => {
        if (attachmentsRequestKeyRef.current !== attachmentsRequestKey) return
        console.error('Error fetching attachments:', error)
      })
      .finally(() => {
        if (attachmentsRequestKeyRef.current === attachmentsRequestKey) {
          attachmentSectionRef.current?.setLoading(false)
        }
      })
  }, [attachmentsRequestKey, client, state.editData.appointmentId, state.editData.email])

  const openExistingAttachment = useCallback(
    async (attachment: OpenableAttachment) => {
      const email = state.editData.email
      const appointmentId = state.editData.appointmentId
      if (!email || !appointmentId) return

      // Every attachment is downloaded with its real filename, regardless of type (no new tab).
      try {
        const response = await client.get(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/calendar/attachments/content`, {
          params: { email, appointmentId, attachmentId: attachment.id },
          responseType: 'blob',
        })
        const fileName =
          attachment.attachmentKind === 'item' && !attachment.name.toLowerCase().endsWith('.eml')
            ? `${attachment.name}.eml`
            : attachment.name
        saveAs(response.data as Blob, fileName)
      } catch (error) {
        console.error('Error downloading attachment:', error)
        notify("Impossibile scaricare l'allegato.", 'error', 3000)
      }
    },
    [client, state.editData.appointmentId, state.editData.email],
  )

  const setSaveDisabled = useCallback(
    (disabled: boolean) => {
      onSaveDisabledChange?.(disabled)
    },
    [onSaveDisabledChange],
  )

  const invalidateAttachmentsCache = useCallback((requestKey: string | null) => {
    if (!requestKey) return
    if (attachmentsRequestKeyRef.current === requestKey) {
      attachmentSectionRef.current?.setExistingAttachments([])
    }
  }, [])

  useEffect(() => {
    attachmentsRequestKeyRef.current = attachmentsRequestKey
    attachmentSectionRef.current?.setExistingAttachments([])
    attachmentSectionRef.current?.setLoading(false)
  }, [attachmentsRequestKey])

  const openRecurrencePopup = () => {
    const formData = (formRef.current?.instance().option('formData') ?? appointment) as FormAppointment
    const sd = formData['start/date']
    const st = formData['start/time']
    const startDate = sd && st ? combineDateAndTime(new Date(sd), new Date(st)) : new Date()
    originalRecurrenceRuleRef.current = formData.recurrenceRule ?? ''
    recurrenceEditedRef.current = false
    recurrenceSchedulerRef.current?.instance().showAppointmentPopup(
      {
        text: RECURRENCE_POPUP_TITLE,
        startDate,
        endDate: new Date(startDate.getTime() + RECURRENCE_PLACEHOLDER_DURATION_MS),
        recurrenceRule: originalRecurrenceRuleRef.current,
      },
      true,
    )
  }

  const syncRecurrenceButton = () => {
    const formData = (formRef.current?.instance().option('formData') ?? appointment) as FormAppointment
    formRef.current
      ?.instance()
      .getButton('button_series')
      ?.option('type', formData.recurrenceRule ? 'success' : 'default')
  }

  // DevExtreme ricostruisce la regola al salvataggio e scarta le parti che il form non gestisce
  // (es. BYSETPOS, WKST): se l'utente non ha toccato il form si mantiene la regola originale.
  const trackRecurrenceEdits = (event: ShownEvent) => {
    const markEdited = () => {
      recurrenceEditedRef.current = true
    }
    const content = event.component.content()
    for (const eventName of ['pointerdown', 'keydown', 'input']) {
      content.addEventListener(eventName, markEdited, { once: true })
    }
  }

  const handleRecurrencePopupOptionChanged = (event: OptionChangedEvent) => {
    // Passando al gruppo ricorrenza DevExtreme blocca l'altezza su quella del gruppo principale, che qui
    // contiene solo "Ripeti": si torna ad "auto" (il gruppo nascosto è absolute e non conta nell'altezza).
    if (event.name === 'height' && typeof event.value === 'number') {
      setTimeout(() => event.component.option('height', 'auto'))
      return
    }
    // Il popup del form Scheduler mostra il titolo "Nuovo appuntamento" nel gruppo principale
    if (event.name !== 'toolbarItems' || !Array.isArray(event.value)) return
    const [titleItem, ...otherItems] = event.value as ToolbarItem[]
    if (titleItem?.cssClass !== 'dx-toolbar-label' || titleItem.text === RECURRENCE_POPUP_TITLE) return
    event.component.option('toolbarItems', [{ ...titleItem, text: RECURRENCE_POPUP_TITLE }, ...otherItems])
  }

  const confirmRecurrenceRule = (event: SchedulerTypes.AppointmentAddingEvent) => {
    event.cancel = true
    const recurrenceRule = recurrenceEditedRef.current
      ? (event.appointmentData.recurrenceRule ?? '')
      : originalRecurrenceRuleRef.current
    formRef.current?.instance().updateData('recurrenceRule', recurrenceRule)
    syncRecurrenceButton()
    recurrenceSchedulerRef.current?.instance().hideAppointmentPopup()
  }

  // Reset della visibilità dei facoltativi e minuti reminder ad ogni apertura del popup
  useEffect(() => {
    const hasOptional = (state.editData.optionalAttendees?.length ?? 0) > 0
    showOptionalRef.current = hasOptional
    formRef.current?.instance().itemOption('optionalAttendees', 'visible', hasOptional)
    const tagBox = formRef.current?.instance().getEditor('requiredAttendees')
    tagBox?.option('buttons', [buildOptionalToggleButton(hasOptional)])

    const reminderOn = state.editData.isReminderOn ?? true
    formRef.current?.instance().itemOption('reminderMinutesBeforeStart', 'visible', reminderOn)
    syncRecurrenceButton()
  }, [state])

  useEffect(() => {
    const startDate = state.editData['start/date']
    const startTime = state.editData['start/time']
    const endDate = state.editData['end/date']
    const endTime = state.editData['end/time']
    const allDay = state.editData['allDay']

    if (startDate && startTime && endDate && endTime) {
      // Fetch room availability with current appointment times
      fetchRoomAvailability(
        new Date(startDate),
        new Date(startTime),
        !allDay
          ? new Date(endDate)
          : new Date(new Date(endDate).getFullYear(), new Date(endDate).getMonth(), new Date(endDate).getDate() + 1),
        new Date(endTime),
      )
    }
  }, [
    state.editData['start/date'],
    state.editData['start/time'],
    state.editData['end/date'],
    state.editData['end/time'],
  ])

  const handleAllDayToggle = (e: SwitchTypes.ValueChangedEvent) => {
    if (!e.event) return
    if (e.value) {
      formRef.current?.instance().getEditor('start/time')?.option('readOnly', true)
      formRef.current?.instance().getEditor('end/time')?.option('readOnly', true)
      formRef.current?.instance().updateData('start/time', createTime(0, 0))
      formRef.current?.instance().updateData('end/time', createTime(0, 0))
      handleDateTimeChange()
    } else {
      formRef.current?.instance().getEditor('start/time')?.option('readOnly', false)
      formRef.current?.instance().getEditor('end/time')?.option('readOnly', false)
      const startDate: Date = formRef.current?.instance().getEditor('start/date')?.option().value
      formRef.current?.instance().updateData('end/date', startDate)

      const now = new Date()
      const isToday =
        startDate.getFullYear() === now.getFullYear() &&
        startDate.getMonth() === now.getMonth() &&
        startDate.getDate() === now.getDate()

      let startHour: number
      let startMinute: number

      if (isToday) {
        // prossima ora tonda
        startHour = now.getMinutes() > 0 ? now.getHours() + 1 : now.getHours()
        startMinute = 0
      } else {
        startHour = 8
        startMinute = 30
      }
      const startDateTime = createTime(startHour, startMinute)
      const endDateTime = new Date(startDateTime.getTime() + 30 * 60000)
      formRef.current?.instance().updateData('start/time', startDateTime)
      formRef.current?.instance().updateData('end/time', endDateTime)
      handleDateTimeChange()
    }
  }

  const handleSave = async () => {
    setSaveDisabled(true)
    const validationResult = formRef.current?.instance().validate()
    if (!validationResult?.isValid) {
      setSaveDisabled(false)
      return
    }
    try {
      if (memoizedAppointment) {
        loadPanelRef.current?.instance().show()
        const startDate = new Date(memoizedAppointment[`start/date`]!)
        const startTime = new Date(memoizedAppointment[`start/time`]!)
        memoizedAppointment.startDate = combineDateAndTime(startDate, startTime)
        const endDate = new Date(memoizedAppointment[`end/date`]!)
        const endTime = new Date(memoizedAppointment[`end/time`]!)
        memoizedAppointment.endDate = combineDateAndTime(endDate, endTime)
        const newFiles = attachmentSectionRef.current?.getNewFiles() ?? []
        const removedAttachmentIds = attachmentSectionRef.current?.getRemovedAttachmentIds() ?? []
        const hasVisibleAttachments = attachmentSectionRef.current?.hasVisibleAttachments() ?? false
        const attachmentsChanged = newFiles.length > 0 || removedAttachmentIds.length > 0
        const nextHasAttachments = attachmentsChanged
          ? hasVisibleAttachments
          : (memoizedAppointment.hasAttachments ?? false)
        const shouldReloadRecurringData =
          !!memoizedAppointment.recurrenceRule ||
          memoizedAppointment.recurrenceType === 'SeriesMaster' ||
          (!!memoizedAppointment.seriesMasterId &&
            memoizedAppointment.seriesMasterId !== memoizedAppointment.appointmentId)
        if (state.creating) {
          try {
            const insertResult = await schedulerRef.current
              ?.instance()
              .getDataSource()
              .store()
              .insert({ ...memoizedAppointment, newAttachments: newFiles })
            popupRef.current?.instance().hide()
            const newItem = {
              ...memoizedAppointment,
              appointmentId: insertResult?.data?.id,
              email: insertResult?.data?.organizer?.emailAddress?.address,
              isOnlineMeeting: insertResult?.data?.isOnlineMeeting ?? false,
              teamsJoinUrl: insertResult?.data?.onlineMeeting?.joinUrl,
              recurrenceType: memoizedAppointment.recurrenceRule ? 'SeriesMaster' : undefined,
              location: {
                uniqueId: insertResult?.data?.location?.uniqueId,
                locationUri: insertResult?.data?.location?.locationUri,
                displayName: insertResult?.data?.location?.displayName,
              },
              requiredAttendees: toEmailStrings(memoizedAppointment.requiredAttendees as unknown as unknown[]),
              optionalAttendees: toEmailStrings(memoizedAppointment.optionalAttendees as unknown as unknown[]),
              hasAttachments: hasVisibleAttachments,
            }
            if (newFiles.length > 0) {
              const createdRequestKey =
                insertResult?.data?.organizer?.emailAddress?.address && insertResult?.data?.id
                  ? `${insertResult.data.organizer.emailAddress.address}__${insertResult.data.id}`
                  : null
              invalidateAttachmentsCache(createdRequestKey)
            }
            if (shouldReloadRecurringData) {
              schedulerRef.current?.instance().getDataSource().reload()
            } else {
              const currentItemsInsert = schedulerRef.current?.instance().getDataSource().items() ?? []
              loadWithPending([...currentItemsInsert, newItem])
            }
          } catch {
            /* empty */
          } finally {
            loadPanelRef.current?.instance().hide()
          }
        } else {
          try {
            const location = normalizeLocationValue(memoizedAppointment.location)
            const updatedItem = {
              ...memoizedAppointment,
              isOnlineMeeting: memoizedAppointment.isOnlineMeeting ?? false,
              location: {
                uniqueId: location?.userid,
                locationUri: location?.email,
                displayName: location?.fullname,
              },
              requiredAttendees: toEmailStrings(memoizedAppointment.requiredAttendees as unknown as unknown[]),
              optionalAttendees: toEmailStrings(memoizedAppointment.optionalAttendees as unknown as unknown[]),
              newAttachments: newFiles,
              removedAttachmentIds,
              hasAttachments: nextHasAttachments,
            }
            const persistedUpdatedItem = {
              ...updatedItem,
              newAttachments: undefined,
              removedAttachmentIds: undefined,
            }
            const updateResult = await schedulerRef.current
              ?.instance()
              .getDataSource()
              .store()
              .update(memoizedAppointment.appointmentId, updatedItem)
            if (attachmentsChanged) {
              invalidateAttachmentsCache(attachmentsRequestKey)
            }
            const teamsJoinUrl = (updateResult as any)?.data?.onlineMeeting?.joinUrl ?? memoizedAppointment.teamsJoinUrl
            popupRef.current?.instance().hide()
            if (shouldReloadRecurringData) {
              schedulerRef.current?.instance().getDataSource().reload()
            } else {
              const currentItemsUpdate = schedulerRef.current?.instance().getDataSource().items() ?? []
              loadWithPending(
                currentItemsUpdate.map((item: ExtendedAppointment) =>
                  item.appointmentId === memoizedAppointment.appointmentId
                    ? ({
                        ...item,
                        ...persistedUpdatedItem,
                        teamsJoinUrl,
                        recurrenceType:
                          item.recurrenceType === 'Occurrence'
                            ? 'Exception'
                            : ((persistedUpdatedItem.recurrenceRule
                                ? 'SeriesMaster'
                                : persistedUpdatedItem.recurrenceType) ?? item.recurrenceType),
                      } as ExtendedAppointment)
                    : item,
                ),
              )
            }
          } catch {
            /* empty */
          } finally {
            loadPanelRef.current?.instance().hide()
          }
        }
      } else console.error('no data tu submit')
    } catch {
      console.log('error')
    } finally {
      setSaveDisabled(false)
    }
  }

  useImperativeHandle(saveRef, () => ({ save: handleSave }))

  return (
    <>
      <LoadPanel
        ref={loadPanelRef}
        shadingColor="rgba(0,0,0,0.4)"
        showIndicator={true}
        shading={true}
        showPane={true}
        hideOnOutsideClick={false}
        hideOnParentScroll={false}
        position={{ of: `#appointment_popup_form` }}
      />
      {/* Form ricorrenza dello Scheduler (recurrence-editor rimosso in 26.1): il salvataggio aggiorna solo il form padre.
          Niente display:none sul contenitore: il popup DevExtreme non si apre se il suo parent è nascosto. */}
      <div className="recurrence-scheduler-host" aria-hidden="true">
        <Scheduler
          ref={recurrenceSchedulerRef}
          timeZone="Europe/Rome"
          firstDayOfWeek={1}
          onAppointmentAdding={confirmRecurrenceRule}
          editing={{
            popup: {
              width: currentScreenSize.isXSmall ? 320 : 520,
              height: 'auto',
              hideOnOutsideClick: false,
              onShown: trackRecurrenceEdits,
              onOptionChanged: handleRecurrencePopupOptionChanged,
            },
            form: {
              items: [
                { name: 'mainGroup', itemType: 'group', items: [{ name: 'repeatGroup', itemType: 'group' }] },
                { name: 'recurrenceGroup', itemType: 'group' },
              ],
            },
          }}
        />
      </div>
      <div className="calendar-appointment-popup-form">
        <Form
          id={'appointment_popup_form'}
          formData={memoizedAppointment}
          labelLocation={'top'}
          colCount={1}
          ref={formRef}
          readOnly={!editEnabled}
        >
          <GroupItem colCount={2}>
            <GroupItem colCount={2} colSpan={2} visible={true}>
              <SimpleItem
                dataField="text"
                label={{ text: 'Oggetto' }}
                editorOptions={{ placeholder: 'Aggiungi un titolo' }}
              >
                <RequiredRule message="Oggetto obbligatorio"></RequiredRule>
              </SimpleItem>
              <SimpleItem
                dataField="location"
                label={{ text: 'Luogo' }}
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: ArraySourceComposer('userid', roomsDataRef.current),
                  displayExpr: (item: CalendarRoom) => {
                    if (!item) return ''
                    // Mostra il pallino di disponibilità solo per le stanze (email non-null),
                    // non per il testo libero inserito manualmente (email: null)
                    if (item.email == null) return item.fullname
                    return `${!item.isAvailable ? '🔴' : '🟢'} ${item.fullname}`
                  },
                  placeholder: 'Seleziona luogo',
                  searchEnabled: true,
                  showClearButton: true,
                  acceptCustomValue: true,
                  dropDownOptions: {
                    showTitle: true,
                    title: 'Selezionare luogo',
                    hideOnOutsideClick: true,
                  },
                  onCustomItemCreating: (e: any) => {
                    if (!e.text) return
                    const customItem = createCustomLocationItem(e.text)
                    e.customItem = customItem
                    return customItem
                  },
                  buttons: [
                    customButton(
                      'fas fa-home',
                      () => {
                        formRef.current?.instance().updateData('location', createCustomLocationItem('SW'))
                      },
                      !editEnabled,
                      'Smart Working',
                    ),
                    customButton(
                      'fas fa-building',
                      () => {
                        formRef.current?.instance().updateData('location', createCustomLocationItem('QS'))
                      },
                      !editEnabled,
                      'Ufficio',
                    ),
                    { name: 'dropDown', location: 'after' },
                  ],
                }}
              ></SimpleItem>
              <SimpleItem
                colSpan={2}
                dataField="requiredAttendees"
                editorType="dxTagBox"
                label={{ text: 'Partecipanti' }}
                editorOptions={{
                  dataSource: ArraySourceComposer(
                    'id',
                    users.filter((imp) => !('isFakePerson' in imp)),
                  ),
                  placeholder: 'Seleziona partecipanti',
                  acceptCustomValue: true,
                  displayExpr: 'fullname',
                  openOnFieldClick: true,
                  searchEnabled: true,
                  showSelectionControls: true,
                  onValueChanged: (e: TagBoxTypes.ValueChangedEvent) => {
                    if (!e.event) return
                    if ((e.value ?? []).length > 0) {
                      formRef.current?.instance().updateData('isOnlineMeeting', true)
                    } else {
                      // Disabilita Teams solo se anche i facoltativi sono vuoti
                      updateTeamsFlag()
                    }
                  },
                  onCustomItemCreating: (e: TagBoxTypes.CustomItemCreatingEvent) => {
                    const newValue = e.text
                    if (newValue) {
                      const regexp = new RegExp(
                        /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Z\\a-z-]+\.)+[A-Za-z]{2,}))$/,
                      )
                      if (regexp.test(newValue)) {
                        const newUser = createCustomAttendee(newValue)
                        const isEmailInValues = e.component
                          .option()
                          .value?.find((user: user) => user.email === newValue)
                        if (isEmailInValues) {
                          e.customItem = null
                        } else {
                          e.customItem = newUser
                        }
                      } else {
                        e.customItem = null
                      }
                    } else e.customItem = null
                  },
                  buttons: [buildOptionalToggleButton(showOptionalRef.current)],
                }}
              ></SimpleItem>
              <SimpleItem
                colSpan={2}
                dataField="optionalAttendees"
                editorType="dxTagBox"
                label={{ text: 'Partecipanti facoltativi' }}
                visible={(state.editData.optionalAttendees?.length ?? 0) > 0}
                editorOptions={{
                  dataSource: ArraySourceComposer(
                    'id',
                    users.filter((imp) => !('isFakePerson' in imp)),
                  ),
                  displayExpr: 'fullname',
                  placeholder: 'Aggiungi partecipanti facoltativi...',
                  acceptCustomValue: true,
                  applyValueMode: 'instantly',
                  showSelectionControls: true,
                  searchEnabled: true,
                  onValueChanged: (e: TagBoxTypes.ValueChangedEvent) => {
                    if (!e.event) return
                    if ((e.value ?? []).length > 0) {
                      formRef.current?.instance().updateData('isOnlineMeeting', true)
                    } else {
                      updateTeamsFlag()
                    }
                  },
                  onCustomItemCreating: (e: TagBoxTypes.CustomItemCreatingEvent) => {
                    const newValue = e.text
                    if (newValue) {
                      const regexp = new RegExp(
                        /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Z\\a-z-]+\.)+[A-Za-z]{2,}))$/,
                      )
                      if (regexp.test(newValue)) {
                        const newUser = createCustomAttendee(newValue)
                        const isEmailInValues = e.component.option().value?.find((u: user) => u.email === newValue)
                        e.customItem = isEmailInValues ? null : newUser
                      } else {
                        e.customItem = null
                      }
                    } else e.customItem = null
                  },
                }}
              ></SimpleItem>
              <SimpleItem
                dataField="user"
                label={{ text: 'Utente calendario' }}
                editorType="dxSelectBox"
                editorOptions={{
                  buttons: [
                    customButton(
                      'user',
                      () => {
                        formRef.current?.instance().updateData(
                          'user',
                          users.find((item) => item.email === userInfo?.email),
                        )
                      },
                      !state.creating ||
                        !isUserRoleAllowed(userInfo?.roles, [
                          Roles.Supervisor,
                          Roles.GlobalAdministrator,
                          Roles.Administrator,
                          Roles.Organizer,
                        ]),
                      'Me stesso/a',
                    ),
                    { name: 'dropDown', location: 'after' },
                  ],
                  dataSource: ArraySourceComposer(
                    'id',
                    users.filter((imp) => !('isFakePerson' in imp)),
                  ),
                  displayExpr: 'fullname',
                  placeholder: 'Seleziona utente calendario',
                  searchEnabled: true,
                  dropDownOptions: {
                    showTitle: true,
                    title: 'Selezionare utente calendario',
                    hideOnOutsideClick: true,
                  },
                  readOnly:
                    (state.creating &&
                      !isUserRoleAllowed(userInfo?.roles, [
                        Roles.Supervisor,
                        Roles.Administrator,
                        Roles.GlobalAdministrator,
                        Roles.Organizer,
                      ])) ||
                    !state.creating,
                }}
              >
                <RequiredRule message="Utente calendario obbligatorio" />
              </SimpleItem>
              <SimpleItem
                dataField="status"
                label={{ text: 'Disponibilità' }}
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: ['Free', 'WorkingElsewhere', 'Tentative', 'Busy', 'Oof'],
                  displayExpr: (item: string) => {
                    switch (item) {
                      case 'Free':
                        return 'Disponibile'
                      case 'WorkingElsewhere':
                        return 'Altra postazione di lavoro'
                      case 'Tentative':
                        return 'Da confermare'
                      case 'Busy':
                        return 'Occupato'
                      case 'Oof':
                        return 'Fuori sede'
                      default:
                        return ''
                    }
                  },
                  placeholder: 'Seleziona disponibilità',
                  searchEnabled: true,
                  dropDownOptions: {
                    showTitle: true,
                    title: 'Selezionare disponibilità',
                    hideOnOutsideClick: true,
                  },
                }}
              >
                <RequiredRule message="Disponibilità obbligatoria"></RequiredRule>
              </SimpleItem>
            </GroupItem>
            <GroupItem colCount={5} colSpan={2}>
              <SimpleItem
                dataField="start/date"
                editorType={'dxDateBox'}
                label={{ text: 'Data inizio' }}
                editorOptions={{
                  displayFormat: `dd/MM/yyyy`,
                  type: 'date',
                  applyValueMode: 'instantly',
                  onValueChanged: (e: DateBoxTypes.ValueChangedEvent) => {
                    if (!e.event) return
                    const newStart = new Date(e.value)
                    formRef.current?.instance().updateData('start/date', newStart)
                    if (!state.creating && originalDurationDays > 0) {
                      // Modalità modifica: preserva la durata originale
                      const newEnd = new Date(newStart)
                      newEnd.setDate(newEnd.getDate() + originalDurationDays)
                      formRef.current?.instance().updateData('end/date', newEnd)
                    } else {
                      formRef.current?.instance().updateData('end/date', newStart)
                    }
                    handleDateTimeChange()
                  },
                  onOpened: (e: DateBoxTypes.OpenedEvent) => {
                    setSaveDisabled(true)
                  },
                  onClosed: (e: DateBoxTypes.ClosedEvent) => {
                    setSaveDisabled(false)
                  },
                }}
              >
                <RequiredRule message="Data inizio obbligatoria"></RequiredRule>
              </SimpleItem>
              <SimpleItem
                dataField="start/time"
                editorOptions={{
                  displayFormat: 'HH:mm',
                  type: 'time',
                  pickerType: 'list',
                  interval: 30,
                  min: createTime(0, 0),
                  max: createTime(23, 30),
                  onKeyDown: (e: DateBoxTypes.KeyDownEvent) => completeTimeOnTab('start/time', e),
                  onValueChanged: (e: DateBoxTypes.ValueChangedEvent) => {
                    if (!e.event) return
                    const raw = new Date(e.value)
                    // Normalizza alla base 1970 locale per coerenza con min/max
                    const startTime = new Date(1970, 0, 1, raw.getHours(), raw.getMinutes(), 0, 0)
                    formRef.current?.instance().updateData('start/time', startTime)
                    // Nuovi eventi: aggiungi 30 min; editazione: preserva la durata originale
                    const durationMs = state.creating ? 30 * 60000 : originalDurationMs
                    formRef.current?.instance().updateData('end/time', new Date(startTime.getTime() + durationMs))
                    if (!appointment.allDay) handleDateTimeChange()
                  },
                  readOnly:
                    (appointment.allDay !== undefined && appointment.allDay !== null && appointment.allDay) ||
                    !editEnabled,
                }}
                editorType="dxDateBox"
                label={{ text: 'Dalle' }}
                visible={true}
              >
                <RequiredRule message="Data inizio obbligatoria"></RequiredRule>
              </SimpleItem>
              <SimpleItem
                visible={true}
                dataField="end/date"
                editorType={'dxDateBox'}
                label={{ text: 'Data Fine' }}
                editorOptions={{
                  displayFormat: `dd/MM/yyyy`,
                  type: 'date',
                  applyValueMode: 'instantly',
                  readOnly: !editEnabled,
                  onOpened: (e: DateBoxTypes.OpenedEvent) => {
                    setSaveDisabled(true)
                  },
                  onClosed: (e: DateBoxTypes.ClosedEvent) => {
                    setSaveDisabled(false)
                  },
                  onValueChanged: (e: DateBoxTypes.ValueChangedEvent) => {
                    if (!e.event) return
                    handleDateTimeChange()
                  },
                }}
              >
                <CustomRule
                  reevaluate={true}
                  message="Data fine deve essere maggiore della data di inizio"
                  validationCallback={({ value }: ValidationCallbackData) => {
                    if (appointment.allDay !== undefined && appointment.allDay !== null && appointment.allDay) {
                      const startDate = new Date(appointment[`start/date`]!)
                      const start = toDateOnly(startDate)
                      const endDate = new Date(value)
                      const end = toDateOnly(endDate)
                      return end >= start
                    }
                    return true
                  }}
                />
              </SimpleItem>
              <SimpleItem
                dataField="end/time"
                editorOptions={{
                  readOnly:
                    (appointment.allDay !== undefined && appointment.allDay !== null && appointment.allDay) ||
                    !editEnabled,
                  displayFormat: 'HH:mm',
                  type: 'time',
                  pickerType: 'list',
                  interval: 30,
                  min: createTime(0, 0),
                  max: createTime(23, 30),
                  onKeyDown: (e: DateBoxTypes.KeyDownEvent) => completeTimeOnTab('end/time', e),
                  onValueChanged: (e: DateBoxTypes.ValueChangedEvent) => {
                    if (!e.event) return
                    const raw = new Date(e.value)
                    // Normalizza alla base 1970 locale per coerenza con min/max
                    const endTime = new Date(1970, 0, 1, raw.getHours(), raw.getMinutes(), 0, 0)
                    formRef.current?.instance().updateData('end/time', endTime)
                    if (!appointment.allDay) handleDateTimeChange()
                  },
                }}
                editorType="dxDateBox"
                label={{ text: 'Alle' }}
                visible={true}
              >
                <RequiredRule message="Data fine obbligatoria"></RequiredRule>
                <CustomRule
                  reevaluate={true}
                  message="Data fine deve essere maggiore della data di inizio"
                  validationCallback={({ value }: ValidationCallbackData) => {
                    if (appointment.allDay !== undefined && appointment.allDay !== null && !appointment.allDay) {
                      const startDate = new Date(appointment[`start/date`]!)
                      const startTime = new Date(appointment[`start/time`]!)
                      const start = combineDateAndTime(startDate, startTime)
                      const endDate = new Date(appointment[`end/date`]!)
                      const endTime = new Date(value)
                      const end = combineDateAndTime(endDate, endTime)
                      return end > start
                    }
                    return true
                  }}
                />
              </SimpleItem>
              <SimpleItem
                dataField="allDay"
                label={{
                  text: 'Tutta la giornata',
                }}
                editorType={'dxSwitch'}
                editorOptions={{
                  onValueChanged: handleAllDayToggle,
                }}
              />
            </GroupItem>
            <GroupItem colCount={4} colSpan={2}>
              <SimpleItem
                dataField="isOnlineMeeting"
                label={{ text: 'Riunione Teams' }}
                editorType="dxSwitch"
                editorOptions={{
                  disabled: !editEnabled,
                }}
              />
              <SimpleItem
                dataField="isReminderOn"
                label={{ text: 'Promemoria' }}
                editorType="dxSwitch"
                editorOptions={{
                  onValueChanged: (e: SwitchTypes.ValueChangedEvent) => {
                    if (!e.event) return
                    formRef.current?.instance().itemOption('reminderMinutesBeforeStart', 'visible', e.value)
                    if (!e.value) formRef.current?.instance().updateData('reminderMinutesBeforeStart', 0)
                    else formRef.current?.instance().updateData('reminderMinutesBeforeStart', 15)
                  },
                }}
              />
              <SimpleItem
                dataField="reminderMinutesBeforeStart"
                label={{ text: 'Minuti prima' }}
                editorType="dxNumberBox"
                editorOptions={{
                  min: 0,
                  max: 10080,
                  showSpinButtons: true,
                  step: 5,
                }}
              />
              <ButtonItem
                name="button_series"
                visible={
                  state.creating ||
                  (state.editData.recurrenceType !== 'Occurrence' && state.editData.recurrenceType !== 'Exception')
                }
              >
                <ButtonOptions
                  text="Serie"
                  icon="repeat"
                  type={appointment.recurrenceRule ? 'success' : 'default'}
                  stylingMode="outlined"
                  disabled={!editEnabled}
                  onClick={openRecurrencePopup}
                />
              </ButtonItem>
            </GroupItem>
            <GroupItem
              colCount={1}
              colSpan={2}
              visible={
                !state.creating &&
                !!(
                  state.editData.recurrenceType ||
                  (state.editData.isOnlineMeeting &&
                    state.editData.teamsJoinUrl &&
                    foundUser?.email === userInfo?.email)
                )
              }
            >
              <SimpleItem
                label={{ visible: false }}
                render={() => (
                  <div className="appointment-info">
                    {state.editData.isOnlineMeeting &&
                      state.editData.teamsJoinUrl &&
                      foundUser?.email === userInfo?.email && (
                        <span>
                          <a
                            href={state.editData.teamsJoinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="appointment-meeting-link"
                          >
                            Partecipa alla riunione Teams
                          </a>
                        </span>
                      )}
                    {getRecurrenceLabel(state.editData.recurrenceType) && (
                      <span
                        className={`appointment-recurrence-form-badge${state.editData.recurrenceType === 'Exception' ? ' appointment-recurrence-form-badge--exception' : ''}`}
                      >
                        <i className="dx-icon-repeat" />
                        {getRecurrenceLabel(state.editData.recurrenceType)}
                      </span>
                    )}
                  </div>
                )}
              />
            </GroupItem>
            <GroupItem colCount={1} colSpan={2} visible={true}>
              <SimpleItem
                dataField="description"
                label={{ text: 'Note' }}
                editorType="dxHtmlEditor"
                editorOptions={{
                  stylingMode: 'filled',
                  valueType: 'html',
                  toolbar: toolbarHtmlEditor(currentScreenSize),
                  mediaResizing: {
                    enabled: true,
                  },
                  imageUpload: {
                    tabs: ['file', 'url'],
                    fileUploadMode: 'base64',
                  },
                  height: 300,
                  onValueChanged: onValueChangedHtmlEditor,
                  onInitialized: onInitializedHtmlEditor,
                }}
              ></SimpleItem>
            </GroupItem>
            <GroupItem visible={!state.creating && !!(state.editData.createdBy || state.editData.modifiedBy)}>
              <SimpleItem
                label={{ visible: false }}
                render={() => (
                  <div className="appointment-meta-info">
                    {state.editData.createdBy && (
                      <span>
                        <strong>Creato da:</strong>{' '}
                        {users.find((u) => u.email === state.editData.createdBy)?.fullname ?? state.editData.createdBy}
                        {state.editData.createdAt && <> &ndash; {state.editData.createdAt}</>}
                      </span>
                    )}
                    {state.editData.modifiedBy && (
                      <span>
                        <strong>Modificato da:</strong>{' '}
                        {users.find((u) => u.email === state.editData.modifiedBy)?.fullname ??
                          state.editData.modifiedBy}
                        {state.editData.modifiedAt && <> &ndash; {state.editData.modifiedAt}</>}
                      </span>
                    )}
                  </div>
                )}
              />
            </GroupItem>
            <GroupItem colCount={1} colSpan={2} visible={true}>
              <SimpleItem
                dataField="taskId"
                label={{ text: 'Task' }}
                editorType="dxSelectBox"
                editorOptions={{
                  buttons: [
                    customButton(
                      'refresh',
                      () => {
                        tasksQuery.refetch()
                      },
                      tasksQuery.isFetching,
                      'Aggiorna elenco task',
                    ),
                    customButton(
                      'link',
                      () => {
                        const currentTaskId = formRef.current?.instance().option('formData')?.taskId
                        if (!currentTaskId) return
                        window.open(`/tasks/${currentTaskId}`, '_blank')
                      },
                      false,
                      'Dettagli Task',
                    ),
                    {
                      name: 'clear',
                      location: 'after',
                    },
                    { name: 'dropDown', location: 'after' },
                  ],
                  dataSource: ArraySourceComposer('id', tasksQuery.data ?? []),
                  valueExpr: 'id',
                  displayExpr: 'ded_Dis',
                  searchEnabled: true,
                  searchExpr: 'ded_Dis',
                  showClearButton: true,
                  placeholder: 'Nessun task collegato',
                  readOnly: !editEnabled || !canEditTaskLink || tasksQuery.isLoading,
                  dropDownOptions: {
                    showTitle: true,
                    title: 'Selezionare task',
                    hideOnOutsideClick: true,
                  },
                }}
              />
            </GroupItem>
            <GroupItem colCount={1} colSpan={2} visible={true}>
              <SimpleItem
                label={{ visible: false }}
                render={() => (
                  <AttachmentSection
                    key={`${state.creating ? 'create' : (state.editData.appointmentId ?? 'new')}-${state.editData.email ?? 'no-email'}`}
                    ref={attachmentSectionRef}
                    resetKey={`${state.creating ? 'create' : (state.editData.appointmentId ?? 'new')}-${state.editData.email ?? 'no-email'}`}
                    editEnabled={editEnabled}
                    creating={state.creating}
                    hasAttachments={state.editData.hasAttachments}
                    onRequestExistingAttachments={requestExistingAttachments}
                    onOpenExistingAttachment={openExistingAttachment}
                  />
                )}
              />
            </GroupItem>
          </GroupItem>
        </Form>
      </div>
    </>
  )
}

export default AppointmentPopupForm
